import { useEffect, useState } from "react";
import LazyLoad from "react-lazyload";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LockIcon from "@mui/icons-material/Lock";
import SearchIcon from "@mui/icons-material/Search";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import UserAvatar from "../UserAvatar";
import DialogWithMessage from "../../DialogWithMessage";
import {
    insetSurface,
    primaryFocusRing,
    primaryHoverBorder,
    thinScrollbar,
} from "../../../surfaceTints";

function MemberRow({
    isLocked,
    isSelected,
    noun,
    onAdd,
    onRemove,
    user,
}) {
    const action = isSelected ? onRemove : onAdd;

    return (
        <ListItemButton
            onClick={isLocked ? undefined : action}
            aria-disabled={isLocked || undefined}
            sx={{
                minWidth: 0,
                flex: "0 0 auto",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                px: 1.25,
                py: 1,
                opacity: isLocked ? 0.72 : 1,
                transition:
                    "border-color 150ms ease, background-color 150ms ease, box-shadow 170ms ease, transform 170ms ease",
                "&:hover": {
                    borderColor: (theme) =>
                        isLocked
                            ? theme.palette.divider
                            : primaryHoverBorder(theme),
                    backgroundColor: "background.paper",
                    boxShadow: (theme) =>
                        isLocked
                            ? "none"
                            : theme.palette.mode === "dark"
                              ? "0 3px 12px rgba(0, 0, 0, 0.34)"
                              : "0 3px 12px rgba(20, 35, 25, 0.07)",
                },
                "&:active": {
                    transform: isLocked ? "none" : "translateY(0.5px)",
                },
                "&:focus-visible": {
                    outline: primaryFocusRing,
                    outlineOffset: 1,
                },
                "@media (prefers-reduced-motion: reduce)": {
                    transition: "none",
                    "&:active": { transform: "none" },
                },
            }}
        >
            <ListItemAvatar sx={{ minWidth: 52 }}>
                <LazyLoad overflow height={40}>
                    <UserAvatar
                        userOrHub={user}
                        path="users"
                        variant="circular"
                        size="medium"
                    />
                </LazyLoad>
            </ListItemAvatar>
            <ListItemText
                primary={user.fullName}
                secondary={
                    isLocked ? `Inherited ${noun}` : user.customRoleName
                }
                primaryTypographyProps={{
                    noWrap: true,
                    sx: { fontWeight: 800 },
                }}
                secondaryTypographyProps={{
                    noWrap: true,
                }}
                sx={{ minWidth: 0 }}
            />
            {isLocked ? (
                <Tooltip
                    title={`This ${noun} is inherited from a parent hub`}
                >
                    <LockIcon
                        sx={{
                            ml: 1,
                            flexShrink: 0,
                            color: "text.disabled",
                        }}
                    />
                </Tooltip>
            ) : isSelected ? (
                <Tooltip title={`Remove ${noun}`}>
                    <DeleteOutlineIcon
                        sx={{
                            ml: 1,
                            flexShrink: 0,
                            color: "text.secondary",
                        }}
                    />
                </Tooltip>
            ) : (
                <Tooltip title={`Add ${noun}`}>
                    <AddCircleOutlineIcon
                        sx={{
                            ml: 1,
                            flexShrink: 0,
                            color: "primary.main",
                        }}
                    />
                </Tooltip>
            )}
        </ListItemButton>
    );
}

export function MemberColumn({
    emptyMessage,
    filter,
    lockedProperty,
    noun,
    onAdd,
    onFilterChange,
    onRemove,
    pluralNoun,
    selectedColumn,
    title,
    totalCount,
    users,
}) {
    return (
        <Box
            sx={{
                minWidth: 0,
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2.5,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 1.25,
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {totalCount}
                </Typography>
            </Box>

            <TextField
                label={`Search ${title.toLowerCase()} ${pluralNoun}`}
                value={filter}
                onChange={(event) => onFilterChange(event.target.value)}
                fullWidth
                size="small"
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon
                                    sx={{
                                        color: "text.secondary",
                                        fontSize: 20,
                                    }}
                                />
                            </InputAdornment>
                        ),
                    },
                }}
                sx={{
                    mb: 1.25,
                    "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backgroundColor: insetSurface,
                    },
                }}
            />

            {users.length === 0 ? (
                <Box
                    sx={{
                        height: { md: 390 },
                        minHeight: 180,
                        display: "grid",
                        placeItems: "center",
                        px: 2,
                        color: "text.secondary",
                        textAlign: "center",
                    }}
                >
                    <Box>
                        <GroupsOutlinedIcon
                            sx={{ mb: 1, fontSize: 38, opacity: 0.3 }}
                        />
                        <Typography
                            variant="subtitle2"
                            sx={{ color: "text.primary", fontWeight: 800 }}
                        >
                            No {pluralNoun} found
                        </Typography>
                        <Typography variant="body2">
                            {filter ? "Try a different search." : emptyMessage}
                        </Typography>
                    </Box>
                </Box>
            ) : (
                <Box
                    sx={(theme) => ({
                        height: { md: 390 },
                        maxHeight: { xs: 320, md: 390 },
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        overflowY: "auto",
                        pr: 0.5,
                        ...thinScrollbar(theme),
                    })}
                >
                    {users.map((user) => (
                        <MemberRow
                            key={user.key}
                            user={user}
                            noun={noun}
                            isSelected={selectedColumn}
                            isLocked={Boolean(
                                selectedColumn && user[lockedProperty],
                            )}
                            onAdd={() => onAdd(user)}
                            onRemove={() => onRemove(user)}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
}

export default function HubMemberSelectionDialog({
    mode,
    onClose,
    open,
    otherUsers: initialOtherUsers,
    selectedUsers: initialSelectedUsers,
}) {
    const isManagerMode = mode === "managers";
    const noun = isManagerMode ? "manager" : "user";
    const pluralNoun = isManagerMode ? "managers" : "users";
    const lockedProperty = isManagerMode
        ? "cannotRemoveAsManager"
        : "cannotRemoveAsUser";

    const [selectedUsers, setSelectedUsers] = useState([]);
    const [otherUsers, setOtherUsers] = useState([]);
    const [selectionHasChanged, setSelectionHasChanged] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState("");
    const [availableFilter, setAvailableFilter] = useState("");
    const [minimumManagerDialogOpen, setMinimumManagerDialogOpen] =
        useState(false);

    useEffect(() => {
        if (open) {
            setSelectedUsers(initialSelectedUsers);
            setOtherUsers(initialOtherUsers);
            setSelectionHasChanged(false);
            setSelectedFilter("");
            setAvailableFilter("");
        }
    }, [open, initialOtherUsers, initialSelectedUsers]);

    const sortedUsers = (users) =>
        [...users].sort((a, b) =>
            (a.lastName || a.fullName || "").localeCompare(
                b.lastName || b.fullName || "",
                undefined,
                { sensitivity: "base" },
            ),
        );

    const usersMatching = (users, filter) => {
        const searchTerm = filter.trim().toLocaleLowerCase();
        return sortedUsers(users).filter((user) =>
            (user.fullName || "")
                .toLocaleLowerCase()
                .includes(searchTerm),
        );
    };

    const visibleSelectedUsers = usersMatching(
        selectedUsers,
        selectedFilter,
    );
    const visibleAvailableUsers = usersMatching(otherUsers, availableFilter);

    function handleApply() {
        onClose(selectedUsers, otherUsers, selectionHasChanged);
    }

    function handleCancel() {
        onClose(undefined, undefined, false);
    }

    function handleSelect(user) {
        setOtherUsers((currentUsers) =>
            currentUsers.filter((candidate) => candidate.key !== user.key),
        );
        setSelectedUsers((currentUsers) => [...currentUsers, user]);
        setSelectionHasChanged(true);
    }

    function handleDelete(user) {
        if (isManagerMode && selectedUsers.length === 1) {
            setMinimumManagerDialogOpen(true);
            return;
        }

        setSelectedUsers((currentUsers) =>
            currentUsers.filter((candidate) => candidate.key !== user.key),
        );
        setOtherUsers((currentUsers) => [...currentUsers, user]);
        setSelectionHasChanged(true);
    }

    return (
        <>
            <Dialog
                onClose={handleCancel}
                open={open}
                fullWidth
                maxWidth="lg"
                aria-labelledby={`edit-${pluralNoun}-dialog-title`}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            overflow: "hidden",
                            maxHeight: "92vh",
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        px: { xs: 2, sm: 3 },
                        py: 2.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            color: "primary.main",
                            backgroundColor: "action.hover",
                        }}
                    >
                        {isManagerMode ? (
                            <ShieldOutlinedIcon />
                        ) : (
                            <GroupsOutlinedIcon />
                        )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            id={`edit-${pluralNoun}-dialog-title`}
                            variant="h5"
                            component="h2"
                            sx={{ lineHeight: 1.1 }}
                        >
                            Edit {pluralNoun}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                        >
                            Add or remove the {pluralNoun} assigned to this hub.
                            These changes will not be saved until you save the
                            hub.
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label={`Close ${pluralNoun} editor`}
                        onClick={handleCancel}
                        sx={{ mt: -0.75, mr: -1 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent
                    dividers
                    sx={{
                        p: { xs: 1.5, sm: 2.5 },
                        backgroundColor: "background.default",
                    }}
                >
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "minmax(0, 1fr)",
                                md: "repeat(2, minmax(0, 1fr))",
                            },
                            gap: 2,
                        }}
                    >
                        <MemberColumn
                            title="Selected"
                            users={visibleSelectedUsers}
                            noun={noun}
                            pluralNoun={pluralNoun}
                            filter={selectedFilter}
                            onFilterChange={setSelectedFilter}
                            emptyMessage={`No ${pluralNoun} are selected.`}
                            selectedColumn
                            lockedProperty={lockedProperty}
                            totalCount={selectedUsers.length}
                            onAdd={handleSelect}
                            onRemove={handleDelete}
                        />
                        <MemberColumn
                            title="Available"
                            users={visibleAvailableUsers}
                            noun={noun}
                            pluralNoun={pluralNoun}
                            filter={availableFilter}
                            onFilterChange={setAvailableFilter}
                            emptyMessage={`There are no more ${pluralNoun} available.`}
                            selectedColumn={false}
                            lockedProperty={lockedProperty}
                            totalCount={otherUsers.length}
                            onAdd={handleSelect}
                            onRemove={handleDelete}
                        />
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
                    <Button onClick={handleCancel} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleApply}
                        variant="contained"
                        disableElevation
                    >
                        Apply to draft
                    </Button>
                </DialogActions>
            </Dialog>

            <DialogWithMessage
                open={minimumManagerDialogOpen}
                onClose={() => setMinimumManagerDialogOpen(false)}
                title="Manager required"
                message="At least one manager must be selected."
            />
        </>
    );
}
